import { LEGAL_EMAIL } from "@Common/constants";
import { PublicFooter } from "@Src/components/PublicFooter";
import { PublicNav } from "@Src/components/PublicNav";
import { Box, Container, Typography } from "@mui/material";

export const TermsPage = () => {
	return (
		<Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
			<PublicNav />

			<Box sx={{ pt: { xs: 8, md: 12 }, pb: { xs: 8, md: 12 } }}>
				<Container maxWidth="md">
					<Typography
						variant="h1"
						sx={{
							fontSize: { xs: "2.5rem", md: "3.5rem" },
							fontWeight: 700,
							color: "text.primary",
							mb: 2,
							textAlign: "center",
						}}
					>
						Terms of Service
					</Typography>

					<Typography
						variant="body2"
						sx={{
							color: "text.secondary",
							mb: 6,
							textAlign: "center",
						}}
					>
						Last updated: {new Date().toLocaleDateString()}
					</Typography>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Acceptance of Terms
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							By accessing and using NoMachine's services, you accept and agree
							to be bound by these Terms of Service. If you do not agree to
							these terms, please do not use our service.
						</Typography>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Service Description
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							NoMachine provides a SaaS platform for creating and managing
							assessments designed to prevent AI interference. Our service
							includes:
						</Typography>
						<Box
							component="ul"
							sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}
						>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Assessment creation and management tools
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									AI prevention mechanisms
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Analytics and reporting features
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Data storage and security
								</Typography>
							</li>
						</Box>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							User Accounts
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							To use our service, you must create an account. You are
							responsible for:
						</Typography>
						<Box
							component="ul"
							sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}
						>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Maintaining the confidentiality of your account credentials
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									All activities that occur under your account
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Notifying us immediately of any unauthorized use
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Providing accurate and current information
								</Typography>
							</li>
						</Box>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Acceptable Use
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							You agree not to:
						</Typography>
						<Box
							component="ul"
							sx={{ color: "text.secondary", lineHeight: 1.8, mb: 2 }}
						>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Use the service for any illegal purpose
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Attempt to gain unauthorized access to our systems
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Interfere with or disrupt the service
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Upload malicious code or content
								</Typography>
							</li>
							<li>
								<Typography variant="body1" sx={{ mb: 1 }}>
									Violate any applicable laws or regulations
								</Typography>
							</li>
						</Box>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Intellectual Property
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							The NoMachine service, including its original content, features,
							and functionality, is owned by NoMachine and is protected by
							international copyright, trademark, and other intellectual
							property laws.
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							You retain ownership of the content you create using our service,
							including assessments and questions. By using our service, you
							grant us a limited license to store, process, and display your
							content as necessary to provide the service.
						</Typography>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Payment and Subscription
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							Paid subscriptions are billed in advance on a monthly or annual
							basis. You can cancel your subscription at any time, but refunds
							are not provided for partial months or unused time. We reserve the
							right to modify our pricing with 30 days' notice.
						</Typography>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Limitation of Liability
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							To the maximum extent permitted by law, NoMachine shall not be
							liable for any indirect, incidental, special, consequential, or
							punitive damages, or any loss of profits or revenues, whether
							incurred directly or indirectly.
						</Typography>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Service Availability
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							We strive to maintain high availability but cannot guarantee
							uninterrupted access to our service. We may modify, suspend, or
							discontinue the service at any time with reasonable notice.
						</Typography>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Termination
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							We may terminate or suspend your account immediately, without
							prior notice, if you breach these Terms of Service. Upon
							termination, your right to use the service will immediately cease.
						</Typography>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Changes to Terms
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								mb: 2,
								lineHeight: 1.8,
							}}
						>
							We reserve the right to modify these terms at any time. We will
							notify users of any material changes via email or through our
							service. Your continued use of the service after such notification
							constitutes acceptance of the new terms.
						</Typography>
					</Box>

					<Box sx={{ mb: 6 }}>
						<Typography
							variant="h4"
							sx={{
								fontWeight: 600,
								color: "text.primary",
								mb: 2,
							}}
						>
							Contact Information
						</Typography>
						<Typography
							variant="body1"
							sx={{
								color: "text.secondary",
								lineHeight: 1.8,
							}}
						>
							If you have questions about these Terms of Service, please contact
							us at {LEGAL_EMAIL}.
						</Typography>
					</Box>
				</Container>
			</Box>

			<PublicFooter />
		</Box>
	);
};
